import React, { useState } from "react";
import axios from "axios";
import "semantic-ui-css/semantic.min.css";
import { useAuth } from "../../../hook/userAuth";
import { Navigate } from "react-router-dom";
import { BASE_API } from "../../../ruta-server/BaseApi";
import {
  Form,
  Button,
  Radio,
  Dropdown,
  Message,
  Table,
  Grid,
} from "semantic-ui-react";
import Docxtemplater from "docxtemplater";
import PizZip from "pizzip";
import { saveAs } from "file-saver";

function BoletasAlumnos() {
  const { auth } = useAuth();
  if (!auth?.me?.es_personal_control_escolar) {
    return <Navigate to={"/"}></Navigate>;
  }
  return <LogicaBoletasAlumnos />;
}

function LogicaBoletasAlumnos() {
  const { auth } = useAuth();
  const [searchType, setSearchType] = useState("matricula");
  const [searchValue, setSearchValue] = useState("");
  const [selectedSemester, setSelectedSemester] = useState("");
  const [alumnoData, setAlumnoData] = useState(null);
  const [calificacionesData, setCalificacionesData] = useState(null);
  const [error, setError] = useState(null);
  const [noCalificacionesMessage, setNoCalificacionesMessage] = useState(false);

  const semesterOptions = [
    { key: 1, text: "I SEMESTRE", value: 1 },
    { key: 2, text: "II SEMESTRE", value: 2 },
    { key: 3, text: "III SEMESTRE", value: 3 },
    { key: 4, text: "IV SEMESTRE", value: 4 },
    { key: 5, text: "V SEMESTRE", value: 5 },
    { key: 6, text: "VI SEMESTRE", value: 6 },
    { key: 7, text: "VII SEMESTRE", value: 7 },
    { key: 8, text: "VIII SEMESTRE", value: 8 },
  ];

  const handleSearchSubmit = async (event) => {
    event.preventDefault();
    if (!searchValue.trim()) {
      setError("Por favor, ingrese un valor antes de buscar.");
      return;
    }

    setError(false);

    try {
      const searchParam = searchType === "matricula" ? "matricula" : "curp";
      const response = await axios.get(
        `${BASE_API}/api/alumno/?${searchParam}=${searchValue}`,
        {
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        }
      );
      const alumno = response.data[0];
      setAlumnoData(alumno);
      setNoCalificacionesMessage(false);

      const calificacionesResponse = await axios.get(
        `${BASE_API}/api/calificacion_curso/?alumno=${alumno.id}`,
        {
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        }
      );

      const calificacionesFiltradas = calificacionesResponse.data.filter(
        (calificacion) => {
          const semestreAlumno = convertirNumeroARomano(
            selectedSemester || alumno.semestre
          );
          const semestreMateria = calificacion.curso_data.materia_data.semestre;
          return semestreAlumno === semestreMateria;
        }
      );

      if (calificacionesFiltradas.length === 0) {
        setNoCalificacionesMessage(true);
      } else {
        // Ordenar las calificaciones por materia_data.id
        calificacionesFiltradas.sort(
          (a, b) => a.curso_data.materia_data.id - b.curso_data.materia_data.id
        );
      }

      setCalificacionesData(calificacionesFiltradas);
    } catch (error) {
      setError("No hay registro de este alumno");
      setAlumnoData(null);
      setCalificacionesData(null);
      console.error("Error fetching data", error);
    }
  };

  const convertirNumeroARomano = (numero) => {
    switch (numero) {
      case 1:
        return "I";
      case 2:
        return "II";
      case 3:
        return "III";
      case 4:
        return "IV";
      case 5:
        return "V";
      case 6:
        return "VI";
      case 7:
        return "VII";
      case 8:
        return "VIII";
      default:
        return "";
    }
  };

  const obtenerCalificacionConRegularizacion = (calificacion) => {
    const calFinal = parseFloat(calificacion?.calificacion_final);
    if (calFinal === 5) {
      const reg1 = parseFloat(calificacion?.regularizacion_1);
      if (reg1 > 5) return { cal: reg1, tipo: "Regularización" };
      if (reg1 === 5) {
        const reg2 = parseFloat(calificacion?.regularizacion_2);
        if (reg2 > 5) return { cal: reg2, tipo: "Regularización" };
        if (reg2 === 5) return { cal: 5, tipo: "Normal" };
      }
    }
    return { cal: calFinal, tipo: "Normal" };
  };

  const calcularPromedio = (calificaciones) => {
    if (!calificaciones || calificaciones.length === 0) return 0;

    // Verificar si hay al menos una calificación pendiente o nula
    const tienePendientes = calificaciones.some((calificacion) => {
      const calificacionFinal =
        obtenerCalificacionConRegularizacion(calificacion).cal;
      return calificacionFinal === null || isNaN(parseFloat(calificacionFinal));
    });

    if (tienePendientes) {
      return "Pendiente";
    }

    // Calcular el promedio solo con calificaciones válidas
    const suma = calificaciones.reduce((total, calificacion) => {
      const calificacionFinal =
        obtenerCalificacionConRegularizacion(calificacion).cal;
      const calificacionNumerica = parseFloat(calificacionFinal);
      return total + calificacionNumerica;
    }, 0);

    const promedio = suma / calificaciones.length;
    return promedio.toFixed(3);
  };

  const obtenerGrado = (semestre) => {
    const gradoMap = {
      1: 1,
      2: 1,
      3: 2,
      4: 2,
      5: 3,
      6: 3,
      7: 4,
      8: 4,
    };

    return gradoMap[semestre] || "";
  };

  const generateDocument = async () => {
    try {
      const templateResponse = await axios.get(
        `${BASE_API}/api/boleta-template/`,
        {
          responseType: "arraybuffer",
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        }
      );

      const zip = new PizZip(templateResponse.data);
      const doc = new Docxtemplater(zip);

      const cicloEscolarId =
        calificacionesData?.[0]?.curso_data?.periodo_data?.Ciclo_escolar;

      if (!cicloEscolarId) {
        setError(
          "No se puede generar esta boleta porque el alumno aún no cursa este semestre o no está registrado en sus cursos correspondientes"
        );
        return;
      }

      const cicloEscolarResponse = await axios.get(
        `${BASE_API}/api/ciclo_escolar/${cicloEscolarId}`,
        {
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        }
      );

      const { fecha_inicio, fecha_fin } = cicloEscolarResponse.data;
      const fechaInicio = new Date(fecha_inicio);
      const fechaFin = new Date(fecha_fin);
      const cicloEscolar = `${fechaInicio.getFullYear()}-${fechaFin.getFullYear()}`;

      const calificaciones = {};
      for (let i = 1; i <= 9; i++) {
        calificaciones[`curso_${i}`] = calificacionesData?.[i - 1]
          ? calificacionesData[i - 1].curso_data.materia_data.nombre_materia
          : "";
        calificaciones[`calificacion_final_${i}`] = calificacionesData?.[i - 1]
          ? obtenerCalificacionConRegularizacion(calificacionesData[i - 1]).cal
          : "";
      }
      const sexoAlumno = alumnoData?.sexo;
      const alumnoTexto =
        sexoAlumno === "H"
          ? "EL ALUMNO"
          : sexoAlumno === "M"
          ? "LA ALUMNA"
          : "";

      const planEstudio =
        calificacionesData?.[0]?.curso_data?.materia_data?.plan_estudio_data
          ?.descripcion;
      const planEstudioMayusculas = planEstudio
        ? planEstudio.toUpperCase()
        : "";

      const semestre = alumnoData?.semestre || 0;
      const grado = obtenerGrado(semestre);

      // Obtener el número de grupo
      const numeroGrupo =
        calificacionesData?.[0]?.curso_data?.grupo_data?.numero_grupo;

      // Asignar el número de grupo a grupo
      const grupo = numeroGrupo || "";

      const fechaActual = new Date().toLocaleDateString("es-MX", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      const fechaEnMayusculas = fechaActual.toUpperCase();

      doc.setData({
        ciclo_escolar: cicloEscolar,
        alumno: alumnoTexto,
        nombre_completo:
          `${alumnoData?.nombre} ${alumnoData?.apellido_paterno} ${alumnoData?.apellido_materno}`.toUpperCase() ||
          "",
        plan_estudio: planEstudioMayusculas,
        grado: grado.toString(), // Convertir a cadena
        grupo: grupo.toString(), // Convertir a cadena
        ...calificaciones,
        semestre: convertirNumeroARomano(alumnoData?.semestre || 1),
        matricula: alumnoData?.matricula || "",
        promedio: calcularPromedio(calificacionesData),
        fecha: fechaEnMayusculas,
      });
      doc.render();
      const output = doc.getZip().generate({
        type: "blob",
        mimeType:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      saveAs(output, "BoletaAlumno.docx");
    } catch (error) {
      console.error("Error fetching template:", error);
      setError(
        "No se puede generar esta boleta porque el alumno aún no cursa este semestre"
      );
    }
  };

  return (
    <>
      <h1 className="tituloin">Boletas</h1>

      <h3>Buscar alumno</h3>
      <Form className="form-inline" onSubmit={handleSearchSubmit}>
        <Form.Group inline>
          <Form.Field>
            <Radio
              label="Matrícula"
              name="searchType"
              value="matricula"
              checked={searchType === "matricula"}
              onChange={(e, { value }) => setSearchType(value)}
            />
          </Form.Field>
          <Form.Field>
            <Radio
              label="CURP"
              name="searchType"
              value="curp"
              checked={searchType === "curp"}
              onChange={(e, { value }) => setSearchType(value)}
            />
          </Form.Field>
          <Form.Field>
            <input
              type="text"
              placeholder={
                searchType === "matricula"
                  ? "Ingrese la matrícula"
                  : "Ingrese el CURP"
              }
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </Form.Field>
          <Form.Field>
            <Dropdown
              placeholder="Seleccione el semestre"
              fluid
              selection
              options={semesterOptions}
              onChange={(e, { value }) => setSelectedSemester(value)}
            />
          </Form.Field>
          <Button type="submit" className="botonEnviar">
            Buscar
          </Button>
        </Form.Group>
      </Form>

      {error && <Message negative>{error}</Message>}

      {alumnoData && (
        <div style={{ textAlign: "center" }}>
          <h2>Datos del Alumno</h2>
          <p>
            <strong>Nombre:</strong> {alumnoData.nombre}{" "}
            {alumnoData.apellido_paterno} {alumnoData.apellido_materno} |{" "}
            <strong>Matrícula:</strong> {alumnoData.matricula} |{" "}
            <strong>Semestre:</strong>{" "}
            {convertirNumeroARomano(alumnoData.semestre)} |{" "}
            <strong>Grado:</strong> {obtenerGrado(alumnoData.semestre)}° |{" "}
            <strong>CURP:</strong> {alumnoData.curp}
          </p>
          <strong>Estado del alumno(a):</strong> {alumnoData.estado_alumno}
        </div>
      )}

      {calificacionesData && (
        <div style={{ textAlign: "center" }}>
          <h2 style={{ marginTop: "10px", marginBottom: "10px" }}>
            Calificaciones
          </h2>
          <Table celled striped textAlign="center">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Clave</Table.HeaderCell>
                <Table.HeaderCell>Curso</Table.HeaderCell>
                <Table.HeaderCell>Semestre</Table.HeaderCell>
                <Table.HeaderCell>Calificación Final</Table.HeaderCell>
                <Table.HeaderCell>Tipo de Curso</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {calificacionesData.map((calificacion, index) => {
                const { cal, tipo } =
                  obtenerCalificacionConRegularizacion(calificacion);
                return (
                  <Table.Row key={index}>
                    <Table.Cell>
                      {calificacion.curso_data.materia_data.clave_materia}
                    </Table.Cell>
                    <Table.Cell>
                      {calificacion.curso_data.materia_data.nombre_materia}
                    </Table.Cell>
                    <Table.Cell>
                      {calificacion.curso_data.materia_data.semestre}
                    </Table.Cell>
                    <Table.Cell>{cal || "Pendiente por registrar"}</Table.Cell>
                    <Table.Cell>{tipo}</Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
            <Table.Footer>
              <Table.Row>
                <Table.HeaderCell colSpan="8">
                  <strong>Promedio:</strong>{" "}
                  {calcularPromedio(calificacionesData)}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          </Table>
        </div>
      )}

      {noCalificacionesMessage && (
        <Message>
          {alumnoData.estado_alumno !== "Activo"
            ? `El alumno se encuentra en estado de ${alumnoData.estado_alumno}`
            : `El alumno no está registrado en ningún curso este semestre. Añádalo en
          los cursos correspondientes a su semestre para generar su historial`}
        </Message>
      )}

      {alumnoData && (
        <Grid centered>
          <Grid.Row>
            <Button onClick={generateDocument} className="descarga">
              Descargar documento
            </Button>
          </Grid.Row>
        </Grid>
      )}
    </>
  );
}

export default BoletasAlumnos;
